import { useCallback, useEffect, useMemo, useState } from 'react';
import useMutation from '../../../hooks/useMutation';
import {
  CreateUserAudioLibraryInput,
  CreateUserAudioLibraryMutation,
} from '../../../graphql/api';
import { RcFile } from 'antd/lib/upload';
import {
  UPLOAD_BUCKET,
  getAudioDurationFromFile,
  getSignedUrl,
  isFileLargerThanLimit,
  uploadPostFile,
  isFileSmallerThanLimit,
  fileMinSize,
} from '../../utils';
import { message } from 'antd';
import {
  addToAudioLibrary,
  setAudioUploadFileId,
  setAudioUploadFileName,
  setAudioUploadProgress,
} from '../../../actions';
import { IState, UserAudioLibrary } from '../../../graphql/types';
import useUser from '../../../hooks/useUser';
import { batch, useDispatch, useSelector } from 'react-redux';
import userAudioLibrarySubscription from '../../../hooks/useSubscription/useAudioLibrarySubscription';
import { v4 as uuid } from 'uuid';
import { createUserAudioLibrary } from '../../../graphql/mutations';

const useAudioUpload = () => {
  const dispatch = useDispatch();
  const audioUploadProgress = useSelector<IState, number>(
    state => state.audioUploadProgress ?? 0
  );
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const audioUploadFileName = useSelector<IState, string | undefined>(
    state => state.audioUploadFileName ?? undefined
  );
  const audioUploadFileId = useSelector<IState, string | undefined>(
    state => state.audioUploadFileId ?? undefined
  );

  const { user } = useUser();
  const { subscribeUpdate } = userAudioLibrarySubscription(user?.userId);

  const createAudioLibraryMutation = useMutation<
    CreateUserAudioLibraryInput,
    CreateUserAudioLibraryMutation
  >(createUserAudioLibrary);

  const s3filePath = useMemo<string>(
    () => `${user?.tenantID}/${user?.userId}/`,
    [user]
  );

  const getDuration = useCallback(
    async (file: RcFile | File) => {
      const maxAudioDuration = user?.plan?.features?.giideMaxLengthAudio
        ?.giideLengthInSeconds
        ? user.plan.features.giideMaxLengthAudio.giideLengthInSeconds
        : 0;

      // console.log(
      //   'from plan: ' +
      //     user?.plan?.features?.giideMaxLengthAudio?.giideLengthInSeconds
      // );

      const duration = await getAudioDurationFromFile(file).catch(err => {
        message.error(err.message);
        return -1;
      });

      // console.log('audio file duration: ' + duration);
      // console.log('maxAudioDuration: ' + maxAudioDuration);

      if (duration > 0 && maxAudioDuration > 0 && duration > maxAudioDuration) {
        const minutes = Math.floor(maxAudioDuration / 60);
        const seconds = maxAudioDuration % 60;
        message.error(
          `Maximum duration length is ${minutes} minutes${
            seconds > 0 ? ` and ${seconds} seconds` : ''
          }`
        );
        return -1;
      }
      return duration;
    },
    [user?.plan]
  );

  const uploadFile = useCallback(
    async (file: RcFile | File, duration: number) => {
      if (file && file.name && user?.userId && user.tenantID) {
        const title = file.name.substr(0, file.name.lastIndexOf('.'));
        const recordingId = uuid();

        batch(() => {
          dispatch(setAudioUploadFileName(title));
          dispatch(setAudioUploadFileId(recordingId));
        });

        const fileKey = `${s3filePath}${recordingId}.${file.name
          .split('.')
          .pop()}`;

        const data = await getSignedUrl(
          fileKey,
          UPLOAD_BUCKET,
          'userRecording',
          'post'
        );

        const { url, fields } = data;

        if (fields && url) {
          const audioLibraryMutationInput = {
            id: recordingId,
            audioUrl: fileKey,
            createdAt: new Date().toISOString(),
            durationSeconds: Math.round(duration),
            tenantID: user?.tenantID,
            title,
            updatedAt: new Date().toISOString(),
            userID: user?.userId,
            state: 'uploaded',
            uploadSource: 'studio',
          };

          try {
            await uploadPostFile(url, file, fields, setProgress);
            await createAudioLibraryMutation.callMutation(
              audioLibraryMutationInput
            );
            dispatch(
              addToAudioLibrary(audioLibraryMutationInput as UserAudioLibrary)
            );
            subscribeUpdate();
          } catch (e) {
            message.error('Something went wrong uploading audio');
          }
        }

        dispatch(setAudioUploadFileName(undefined));
      }
    },
    [
      s3filePath,
      createAudioLibraryMutation,
      dispatch,
      user?.userId,
      user?.tenantID,
      subscribeUpdate,
    ]
  );

  const handleUpload = useCallback(
    async (file: RcFile | File) => {
      if (file) {
        const allowedMimeType = [
          'audio/mpeg',
          'audio/wav',
          'audio/mp3',
          'video/mp4',
          'audio/m4a',
          'audio/x-m4a',
          'audio/vnd.wave',
          'video/3gpp',
          'video/x-m4v',
          'audio/webm',
          'video/webm',
        ];

        const allowedExt = ['.wav', '.mp3', '.mp4', '.m4a', '.webm'];
        const ext = file.name.slice(file.name.lastIndexOf('.'));
        const audioMaxSize =
          user?.plan?.features?.giideMaxFileSizeAudio
            ?.giideMaxFileSizeMBAudio ?? 0;

        const isLargerThanLimit = isFileLargerThanLimit(
          file.size,
          audioMaxSize
        );

        const isSmallerThanLimit = isFileSmallerThanLimit(
          file.size,
          fileMinSize
        );

        if (isLargerThanLimit) {
          message.error(`Maximum audio size is ${audioMaxSize} MB`);
          return false;
        } else if (isSmallerThanLimit) {
          message.error(`Minimum audio size is ${fileMinSize} KB`);
          return false;
        } else if (
          !allowedMimeType.includes(file.type.toLowerCase()) &&
          !allowedExt.includes(ext.toLowerCase())
        ) {
          message.error(
            'Accepted file types: .mp3, .mp4, .m4a, .webm and .wav'
          );
          return false;
        } else {
          const duration = await getDuration(file);

          if (duration > -1) {
            setProgress(1);
            await uploadFile(file, duration);
            setProgress(undefined);
            dispatch(setAudioUploadProgress(undefined));
          }
        }
      } else {
        message.error('Failed to load audio file');
        return false;
      }

      return true;
    },
    [uploadFile, getDuration, dispatch, user?.plan]
  );

  useEffect(() => {
    if (progress) {
      dispatch(setAudioUploadProgress(progress));
    }
  }, [dispatch, progress]);

  return {
    audioUploadProgress,
    handleUpload,
    audioUploadFileName,
    audioUploadFileId,
  };
};

export default useAudioUpload;
